import store from '@/store';
import availablePaymentEngines from '../constants/PaymentEngine';

/**
 * Types of cybersource error codes
 * this should be passed in the setError action to show an error message
 * */
const cybersourceErrorCodes = {
  ach_verification_failed: 'cybersource.ach_verification_failed',
  address_country_watchlist_match: 'cybersource.address_country_watchlist_match',
  allowable_pin_retries_exceeded: 'cybersource.allowable_pin_retries_exceeded',
  avs_failed: 'cybersource.avs_failed',
  blacklisted_customer: 'cybersource.blacklisted_customer',
  blocked_by_cardholder: 'cybersource.blocked_by_cardholder',
  consumer_authentication_failed: 'cybersource.consumer_authentication_failed',
  consumer_authentication_required: 'cybersource.consumer_authentication_required',
  contact_processor: 'cybersource.contact_processor',
  customer_watchlist_match: 'cybersource.customer_watchlist_match',
  cv_failed: 'cybersource.cv_failed',
  cvn_not_match: 'cybersource.cvn_not_match',
  daggdenied: 'cybersource.daggdenied',
  daggrejected: 'cybersource.daggrejected',
  decision_profile_reject: 'cybersource.decision_profile_reject',
  decision_profile_review: 'cybersource.decision_profile_review',
  declined_check: 'cybersource.declined_check',
  dsysrejected: 'cybersource.dsysrejected',
  email_country_watchlist_match: 'cybersource.email_country_watchlist_match',
  exceeds_credit_limit: 'cybersource.exceeds_credit_limit',
  expired_card: 'cybersource.expired_card',
  general_decline: 'cybersource.general_decline',
  insufficient_fund: 'cybersource.insufficient_fund',
  invalid_account: 'cybersource.invalid_account',
  invalid_cvn: 'cybersource.invalid_cvn',
  invalid_merchant_configuration: 'cybersource.invalid_merchant_configuration',
  ip_country_watchlist_match: 'cybersource.ip_country_watchlist_match',
  issuer_unavailable: 'cybersource.issuer_unavailable',
  payment_refused: 'cybersource.payment_refused',
  pending_authentication: 'cybersource.pending_authentication',
  processor_declined: 'cybersource.processor_declined',
  processor_error: 'cybersource.processor_error',
  score_exceeds_threshold: 'cybersource.score_exceeds_threshold',
  stolen_lost_card: 'cybersource.stolen_lost_card',
  suspended_account: 'cybersource.suspended_account',
  unauthorized_card: 'cybersource.unauthorized_card',
  default: 'cybersource.default_error',
};

/**
 * Resolve the notification key for a Cybersource decline reason.
 * Own-property check so inherited keys (e.g. "toString", "constructor",
 * "__proto__") fall back to the default error instead of resolving
 * Object.prototype members.
 * @param {String} reason
 * @returns {String} notification key (e.g. 'cybersource.expired_card')
 */
export function getCybersourceErrorKey(reason) {
  return Object.prototype.hasOwnProperty.call(cybersourceErrorCodes, reason)
    ? cybersourceErrorCodes[reason]
    : cybersourceErrorCodes.default;
}

/**
 * Flag to indicate if it should use cybersource or not
 * @param {String} paymentEngine
 * @returns Boolean
 */
export function shouldUseCybersource(paymentEngine) {
  const cybersourceIsEnabled = store.getState().whitelabelConfig?.env?.cybersource?.enabled;
  return cybersourceIsEnabled && paymentEngine === availablePaymentEngines.cybersource;
}

/**
 * Decode the base64url-encoded payload segment of a JWT without verifying its signature.
 * @param {String} jwt - The JWT string (header.payload.signature)
 * @returns {Object|null} The decoded payload object, or null if it cannot be decoded
 */
function decodeJwtPayload(jwt) {
  try {
    const payloadSegment = jwt?.split('.')?.[1];
    if (!payloadSegment) return null;

    const base64 = payloadSegment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    return JSON.parse(atob(padded));
  } catch (error) {
    console.error('Failed to decode Cybersource transient token:', error);
    return null;
  }
}

/**
 * Extract the card BIN from a Cybersource transient token (microform JWT).
 * The microform hosted fields never expose raw digits in field events; the BIN
 * is only available in the transient token payload at
 * content.paymentInformation.card.number.bin.
 * @param {String} transientToken - The transient token JWT returned by microform.createToken
 * @returns {String|null} The BIN (typically 6 digits), or null if unavailable
 */
export function getBinFromCybersourceToken(transientToken) {
  const payload = decodeJwtPayload(transientToken);
  return payload?.content?.paymentInformation?.card?.number?.bin || null;
}

export default cybersourceErrorCodes;
